import { Controller } from "@hotwired/stimulus"

const KEY = "mf_consent_v1"

export default class extends Controller {
  static targets = ["banner", "panel", "analyticsToggle", "preferenceToggle"]
  static values = { gtmId: String }

  connect() {
    const consent = this.#load()
    if (consent) {
      this.#apply(consent)
    } else if (this.hasBannerTarget) {
      requestAnimationFrame(() => this.#showBanner())
    } else if (this.gtmIdValue) {
      // Paid pages render no cookie banner. Imply consent so GTM / conversion
      // tracking still fires instead of waiting on a banner that never shows.
      this.#loadGTM(this.gtmIdValue)
    }
  }

  acceptAll() {
    const c = { necessary: true, analytics: true, preference: true }
    this.#save(c); this.#apply(c); this.#hideBanner(); this.#hidePanel()
  }

  rejectNonEssential() {
    const c = { necessary: true, analytics: false, preference: false }
    this.#save(c); this.#hideBanner(); this.#hidePanel()
  }

  manage() {
    const c = this.#load() || {}
    this.#setToggle(this.analyticsToggleTarget,  c.analytics  !== false)
    this.#setToggle(this.preferenceToggleTarget, c.preference !== false)
    if (this.hasPanelTarget) this.panelTarget.classList.remove("hidden")
  }

  toggleAnalytics() {
    const on = this.analyticsToggleTarget.getAttribute("aria-checked") === "true"
    this.#setToggle(this.analyticsToggleTarget, !on)
  }

  togglePreference() {
    const on = this.preferenceToggleTarget.getAttribute("aria-checked") === "true"
    this.#setToggle(this.preferenceToggleTarget, !on)
  }

  savePreferences() {
    const c = {
      necessary:  true,
      analytics:  this.analyticsToggleTarget.getAttribute("aria-checked")  === "true",
      preference: this.preferenceToggleTarget.getAttribute("aria-checked") === "true",
    }
    this.#save(c); this.#apply(c); this.#hideBanner(); this.#hidePanel()
  }

  closePanel() { this.#hidePanel() }

  openPreferences(e) {
    e.preventDefault()
    this.manage()
  }

  // ── private ──────────────────────────────────────────────────────────

  #load() {
    try { const r = localStorage.getItem(KEY); return r ? JSON.parse(r) : null } catch { return null }
  }

  #save(c) {
    try { localStorage.setItem(KEY, JSON.stringify({ ...c, ts: Date.now() })) } catch {}
  }

  #apply(c) {
    if (c.analytics && this.gtmIdValue) this.#loadGTM(this.gtmIdValue)
  }

  #loadGTM(id) {
    if (window._mfGtmLoaded) return
    window._mfGtmLoaded = true
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" })
    const s = document.createElement("script")
    s.async = true
    s.src = `https://www.googletagmanager.com/gtm.js?id=${id}`
    document.head.appendChild(s)
  }

  #setToggle(btn, on) {
    if (!btn) return
    btn.setAttribute("aria-checked", String(on))
    btn.classList.toggle("bg-primary", on)
    btn.classList.toggle("bg-hairline-soft", !on)
    btn.classList.toggle("dark:bg-zinc-700", !on)
    const knob = btn.querySelector("span")
    if (knob) {
      knob.classList.toggle("translate-x-5", on)
      knob.classList.toggle("translate-x-0", !on)
    }
  }

  #showBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove("translate-y-4", "opacity-0", "pointer-events-none")
    }
  }

  #hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("translate-y-4", "opacity-0", "pointer-events-none")
    }
  }

  #hidePanel() {
    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")
  }
}
